import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { AuthContext } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import ToolSelector from '../../components/ToolSelector';
import PageTutorial from '../../components/PageTutorial';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { stringifyError } from '../../utils/errors';
import {
 ArrowLeft, Plus, Edit, Trash2, BookOpen, List, MessageSquare,
 Save, X, AlertCircle, Sparkles, ChevronDown, ChevronUp
} from 'lucide-react';
import { 
 RichTextEditor, 
 VideoInput, 
 DocumentInput, 
 DownloadManager, 
 ImageManager 
} from '../../components/ContentEditors';
import CourseCard from '../../components/admin/lms/CourseCard';
import CourseForm from '../../components/admin/lms/CourseForm';
import AIContentHealthCard from '../../components/admin/lms/AIContentHealthCard';
import CourseResearchModal from '../../components/admin/lms/CourseResearchModal';
import ModuleForm from '../../components/admin/lms/ModuleForm';
import LessonForm from '../../components/admin/lms/LessonForm';
import QuizForm from '../../components/admin/lms/QuizForm';
import SupportRequestsTab from '../../components/admin/lms/SupportRequestsTab';
import CourseBlueprintModal from '../../components/CourseSyllabusModal';
import CurriculumMap from '../../components/admin/lms/CurriculumMap';
import { useContentSections, useQuizBuilder, useLMSForms } from '../../hooks/lms';

const LMSAdmin = () => {
 const navigate = useNavigate();
 const location = useLocation();
 const { API } = useContext(AuthContext);
 // Track whether we've already honored the ?course=<id> deep-link so
 // toggling other courses on the page doesn't keep re-scrolling.
 const initialCourseParamHandled = useRef(false);

 // Master AI course builder — modal + suggestions panel state
 const [showBlueprintModal, setShowBlueprintModal] = useState(false);
 const [blueprintSeed, setBlueprintSeed] = useState(null);
 const [suggestions, setSuggestions] = useState([]);
 const [suggestionsLoading, setSuggestionsLoading] = useState(false);
 const [suggestionTier, setSuggestionTier] = useState('any'); // any | free | pro | business | enterprise

 const loadCourseSuggestions = async () => {
 setSuggestionsLoading(true);
 try {
 const params = new URLSearchParams({ limit: '5' });
 if (suggestionTier && suggestionTier !== 'any') params.set('target_tier', suggestionTier);
 const { data } = await axios.get(`${API}/admin/ai/course-suggestions?${params.toString()}`);
 setSuggestions(data?.suggestions || []);
 } catch {
 toast.error('Could not load AI course suggestions');
 } finally {
 setSuggestionsLoading(false);
 }
 };
 
 // Core state
 const [activeTab, setActiveTab] = useState('courses');
 const [courses, setCourses] = useState([]);
 // Course Research modal (KB-first + web) — holds the course being researched
 const [researchCourse, setResearchCourse] = useState(null);
 // Courses tab overview filters — state lives at the tab level so the
 // filter chips and search box drive the same rendered list.
 const [coursesFilter, setCoursesFilter] = useState('all'); // all|drafts|published|private
 const [coursesTierFilter, setCoursesTierFilter] = useState('all');
 const [coursesSearch, setCoursesSearch] = useState('');
 const [supportRequests, setSupportRequests] = useState([]);
 const [loading, setLoading] = useState(false);
 const [selectedCourse, setSelectedCourse] = useState(null);
 const [selectedModule, setSelectedModule] = useState(null);
 const [selectedLesson, setSelectedLesson] = useState(null);
 const [expandedModules, setExpandedModules] = useState({});
 const [courseDetails, setCourseDetails] = useState({});

 // Custom hooks for state management
 const {
 showCourseForm,
 setShowCourseForm,
 showModuleForm,
 setShowModuleForm,
 showLessonForm,
 setShowLessonForm,
 showQuizForm,
 setShowQuizForm,
 editingCourse,
 setEditingCourse,
 editingModule,
 setEditingModule,
 editingLesson,
 setEditingLesson,
 courseForm,
 setCourseForm,
 moduleForm,
 setModuleForm,
 lessonForm,
 setLessonForm,
 quizForm,
 setQuizForm,
 moduleContentSectionForm,
 setModuleContentSectionForm,
 editingModuleSectionId,
 setEditingModuleSectionId,
 newQuestion,
 setNewQuestion,
 resetCourseForm,
 resetModuleForm,
 resetLessonForm,
 resetQuizForm
 } = useLMSForms();

 const {
 contentSectionForm,
 setContentSectionForm,
 editingSectionId,
 expandedSections,
 setExpandedSections,
 addContentSection: addContentSectionHook,
 editContentSection,
 cancelEditSection,
 removeContentSection: removeContentSectionHook,
 moveContentSection: moveContentSectionHook
 } = useContentSections();

 const {
 quizQuestion,
 setQuizQuestion,
 editingQuestionIndex,
 addQuizQuestion: addQuizQuestionHook,
 editQuizQuestion,
 updateQuizQuestion: updateQuizQuestionHook,
 cancelEditQuizQuestion,
 removeQuizQuestion: removeQuizQuestionHook
 } = useQuizBuilder();

 useEffect(() => {
 fetchCourses();
 if (activeTab === 'support') {
 fetchSupportRequests();
 }
 }, [activeTab, API]);

 const fetchCourses = async () => {
 try {
 setLoading(true);
 const response = await axios.get(`${API}/lms/courses`);
 // Endpoint returns a bare array of courses (see server.py
 // GET /lms/courses). Previous code assumed `{courses: []}`
 // and silently fell back to an empty array — which is why
 // the admin panel showed "No courses yet." even when the DB
 // held courses.
 const payload = response.data;
 const list = Array.isArray(payload)
   ? payload
   : (payload?.courses || []);
 setCourses(list);
 } catch (error) {
 console.error('Error fetching courses:', error);
 toast.error('Failed to load courses');
 } finally {
 setLoading(false);
 }
 };

 const fetchSupportRequests = async () => {
 try {
 const response = await axios.get(`${API}/lms/support-requests`);
 setSupportRequests(response.data.support_requests || []);
 } catch (error) {
 console.error('Error fetching support requests:', error);
 toast.error('Failed to load support requests');
 }
 };

 const fetchCourseDetails = async (courseId) => {
 try {
 const response = await axios.get(`${API}/lms/courses/${courseId}`);
 setCourseDetails(prev => ({
 ...prev,
 [courseId]: response.data
 }));
 } catch (error) {
 console.error('Error fetching course details:', error);
 toast.error('Failed to load course details');
 }
 };

 const toggleCourse = async (course) => {
 if (selectedCourse?.id === course.id) {
 setSelectedCourse(null);
 } else {
 setSelectedCourse(course);
 // Fetch details if not already loaded
 if (!courseDetails[course.id]) {
 await fetchCourseDetails(course.id);
 }
 }
 };

 // Expand a specific course card and scroll it into view. Used by the
 // ?course=<id> deep-link (blueprint "Open now") and by the "Continue
 // editing" quick-jump for AI-built drafts. Navigates to the full
 // workspace so the admin never scrolls to top.
 const openCourseInEditor = async (courseId) => {
 if (!courseId) return;
 navigate(`/admin/lms/course/${courseId}/workspace`);
 };

 // Honor deep-link ?course=<id> once the course list is populated so
 // "Open now" from the AI Blueprint success screen jumps straight to
 // the new draft.
 useEffect(() => {
 if (initialCourseParamHandled.current) return;
 if (!courses || courses.length === 0) return;
 const params = new URLSearchParams(location.search);
 const target = params.get('course');
 if (target) {
 initialCourseParamHandled.current = true;
 openCourseInEditor(target);
 }
 }, [courses, location.search]);

 const toggleModule = (moduleId) => {
 setExpandedModules(prev => ({
 ...prev,
 [moduleId]: !prev[moduleId]
 }));
 };

  const createCourse = async () => {
    try {
      // Prepare course data with proper type conversion
      const courseData = {
        ...courseForm,
        duration_minutes: courseForm.duration_minutes ? parseInt(courseForm.duration_minutes) : null
      };
      
      if (editingCourse) {
        // Update existing course
        await axios.put(`${API}/lms/courses/${editingCourse.id}`, courseData);
        toast.success('Course updated successfully');
        setEditingCourse(null);
      } else {
        // Create new course
        await axios.post(`${API}/lms/courses`, courseData);
        toast.success('Course created successfully');
      }
      
      setShowCourseForm(false);
      resetCourseForm();
      fetchCourses();
    } catch (error) {
      console.error('Error creating course:', error);
      toast.error(stringifyError(error, 'Failed to create course'));
    }
  };

 const editCourse = (course) => {
 setEditingCourse(course);
 setCourseForm({
 title: course.title,
 description: course.description,
 abstract: course.abstract || '',
 web_enrichment_enabled: course.web_enrichment_enabled || false,
 primary_categories: course.primary_categories || [],
 tier: course.tier,
 thumbnail_url: course.thumbnail_url || '',
 instructor: course.instructor || '',
 duration_minutes: course.duration_minutes || '',
 order: course.order || 0,
 is_published: course.is_published !== false,
 visibility: course.visibility || 'public',
 access_code_required: !!course.access_code_required,
 themes: course.themes || [],
 ad_enabled: course.ad_enabled || false,
 ad_title: course.ad_title || '',
 ad_content: course.ad_content || '',
 ad_image_url: course.ad_image_url || '',
 ad_link_url: course.ad_link_url || '',
 ad_link_text: course.ad_link_text || ''
 });
 setShowCourseForm(true);
 };

 const createModule = async () => {
 if (!moduleForm.course_id || !moduleForm.title) {
 toast.error('Please fill all required fields');
 return;
 }
 try {
 if (editingModule) {
 // Update existing module
 await axios.put(`${API}/lms/modules/${editingModule.id}`, moduleForm);
 toast.success('Module updated');
 setEditingModule(null);
 if (selectedCourse) {
 await fetchCourseDetails(selectedCourse.id);
 }
 } else {
 // Create new module
 await axios.post(`${API}/lms/modules`, moduleForm);
 toast.success('Module created');
 }
 resetModuleForm();
 setShowModuleForm(false);
 fetchCourses();
 } catch (error) {
 console.error('Error saving module:', error);
 toast.error('Failed to save module');
 }
 };

 const createLesson = async () => {
 if (!lessonForm.module_id || !lessonForm.title) {
 toast.error('Please fill all required fields');
 return;
 }
 try {
 // Prepare lesson data with proper type conversion
 const lessonData = {
 ...lessonForm,
 duration_minutes: lessonForm.duration_minutes ? parseInt(lessonForm.duration_minutes) : null
 };
 
 if (editingLesson) {
 // Update existing lesson
 await axios.put(`${API}/lms/lessons/${editingLesson.id}`, lessonData);
 toast.success('Lesson updated');
 setEditingLesson(null);
 if (selectedCourse) {
 await fetchCourseDetails(selectedCourse.id);
 }
 } else {
 // Create new lesson
 await axios.post(`${API}/lms/lessons`, lessonData);
 toast.success('Lesson created');
 }
 resetLessonForm();
 setShowLessonForm(false);
 fetchCourses();
 } catch (error) {
 console.error('Error saving lesson:', error);
 toast.error(stringifyError(error, 'Failed to save lesson'));
 }
 };

 const createQuiz = async () => {
 try {
 await axios.post(`${API}/lms/quizzes`, quizForm);
 toast.success('Quiz created successfully');
 setShowQuizForm(false);
 resetQuizForm();
 } catch (error) {
 console.error('Error creating quiz:', error);
 toast.error('Failed to create quiz');
 }
 };

 const deleteCourse = async (courseId) => {
 if (!window.confirm('Are you sure? This will delete all modules, lessons, and related data.')) return;
 
 try {
 await axios.delete(`${API}/lms/courses/${courseId}`);
 toast.success('Course deleted successfully');
 fetchCourses();
 } catch (error) {
 console.error('Error deleting course:', error);
 toast.error('Failed to delete course');
 }
 };

 const editModule = (module) => {
 setEditingModule(module);
 setModuleForm({
 course_id: module.course_id,
 title: module.title,
 description: module.description || '',
 content_sections: module.content_sections || [],
 order: module.order || 0,
 support_photos: module.support_photos || [],
 support_videos: module.support_videos || [],
 support_links: module.support_links || [],
 support_downloads: module.support_downloads || [],
 ad_enabled: module.ad_enabled || false,
 ad_title: module.ad_title || '',
 ad_content: module.ad_content || '',
 ad_image_url: module.ad_image_url || '',
 ad_link_url: module.ad_link_url || '',
 ad_link_text: module.ad_link_text || ''
 });
 setShowModuleForm(true);
 };

 const deleteModule = async (moduleId) => {
 if (!window.confirm('Are you sure you want to delete this module and all its lessons?')) return;
 try {
 await axios.delete(`${API}/lms/modules/${moduleId}`);
 toast.success('Module deleted');
 if (selectedCourse) {
 await fetchCourseDetails(selectedCourse.id);
 }
 fetchCourses();
 } catch (error) {
 console.error('Error deleting module:', error);
 toast.error('Failed to delete module');
 }
 };

 const editLesson = (lesson) => {
 setEditingLesson(lesson);
 setLessonForm({
 module_id: lesson.module_id,
 title: lesson.title,
 description: lesson.description || '',
 content_sections: lesson.content_sections || [],
 content_type: lesson.content_type || 'video',
 video_url: lesson.video_url || '',
 written_content: lesson.written_content || '',
 pdf_url: lesson.pdf_url || '',
 markdown_content: lesson.markdown_content || '',
 duration_minutes: lesson.duration_minutes || '',
 order: lesson.order || 0,
 support_photos: lesson.support_photos || [],
 support_videos: lesson.support_videos || [],
 support_links: lesson.support_links || [],
 support_downloads: lesson.support_downloads || [],
 ad_enabled: lesson.ad_enabled || false,
 ad_title: lesson.ad_title || '',
 ad_content: lesson.ad_content || '',
 ad_image_url: lesson.ad_image_url || '',
 ad_link_url: lesson.ad_link_url || '',
 ad_link_text: lesson.ad_link_text || '',
 ad_position: lesson.ad_position || 'top'
 });
 setShowLessonForm(true);
 };

 const deleteLesson = async (lessonId) => {
 if (!window.confirm('Are you sure you want to delete this lesson?')) return;
 try {
 await axios.delete(`${API}/lms/lessons/${lessonId}`);
 toast.success('Lesson deleted');
 if (selectedCourse) {
 await fetchCourseDetails(selectedCourse.id);
 }
 fetchCourses();
 } catch (error) {
 console.error('Error deleting lesson:', error);
 toast.error('Failed to delete lesson');
 }
 };

 const updateSupportRequest = async (requestId, status, response) => {
 try {
 await axios.put(`${API}/lms/support-requests/${requestId}`, {
 status,
 admin_response: response
 });
 toast.success('Support request updated');
 fetchSupportRequests();
 } catch (error) {
 console.error('Error updating support request:', error);
 toast.error('Failed to update support request');
 }
 };

 const addQuestionToQuiz = () => {
 if (!newQuestion.question || !newQuestion.correct_answer) {
 toast.error('Please fill in question and correct answer');
 return;
 }
 
 setQuizForm(prev => ({
 ...prev,
 questions: [...prev.questions, { ...newQuestion }]
 }));
 
 setNewQuestion({
 question: '',
 type: 'multiple_choice',
 options: ['', '', '', ''],
 correct_answer: '',
 explanation: ''
 });
 };

 const removeQuestion = (index) => {
 setQuizForm(prev => ({
 ...prev,
 questions: prev.questions.filter((_, i) => i !== index)
 }));
 };


 // Content Section Management
 // Content section management (using hooks)
 const addContentSection = () => {
 if (!contentSectionForm.title) {
 toast.error('Please add a section title');
 return;
 }
 if (contentSectionForm.type === 'quiz' && (!contentSectionForm.quiz_data?.questions || contentSectionForm.quiz_data.questions.length === 0)) {
 toast.error('Please add at least one question to the quiz');
 return;
 }
 const updatedSections = addContentSectionHook(lessonForm.content_sections);
 if (updatedSections) {
 setLessonForm({ ...lessonForm, content_sections: updatedSections });
 }
 };

 const removeContentSection = (sectionId) => {
 const updatedSections = removeContentSectionHook(sectionId, lessonForm.content_sections);
 setLessonForm({ ...lessonForm, content_sections: updatedSections });
 };

 const moveContentSection = (index, direction) => {
 const updatedSections = moveContentSectionHook(index, direction, lessonForm.content_sections);
 setLessonForm({ ...lessonForm, content_sections: updatedSections });
 };

 // Quiz question management (using hooks)
 const updateQuizQuestion = () => {
 const updatedQuestions = updateQuizQuestionHook(contentSectionForm.quiz_data.questions);
 if (updatedQuestions) {
 setContentSectionForm({
 ...contentSectionForm,
 quiz_data: { ...contentSectionForm.quiz_data, questions: updatedQuestions }
 });
 }
 };

 // Module Content Section Management Functions
 const addModuleContentSection = () => {
 if (!moduleContentSectionForm.title) {
 toast.error('Please add a section title');
 return;
 }
 
 // Validate quiz sections have questions
 if (moduleContentSectionForm.type === 'quiz') {
 if (!moduleContentSectionForm.quiz_data?.questions || moduleContentSectionForm.quiz_data.questions.length === 0) {
 toast.error('Please add at least one question to the quiz');
 return;
 }
 }
 
 if (editingModuleSectionId) {
 // Update existing section
 const updatedSections = moduleForm.content_sections.map(section =>
 section.id === editingModuleSectionId
 ? { ...section, ...moduleContentSectionForm }
 : section
 );
 
 setModuleForm({
 ...moduleForm,
 content_sections: updatedSections
 });
 
 setEditingModuleSectionId(null);
 toast.success('Content section updated');
 } else {
 // Add new section
 const newSection = {
 id: Date.now().toString(),
 order: moduleForm.content_sections.length,
 ...moduleContentSectionForm
 };
 
 setModuleForm({
 ...moduleForm,
 content_sections: [...moduleForm.content_sections, newSection]
 });
 
 toast.success('Content section added');
 }
 
 // Reset section form
 setModuleContentSectionForm({
 type: 'video',
 title: '',
 video_url: '',
 text_content: '',
 markdown_content: '',
 pdf_url: '',
 presentation_url: '',
 quiz_data: {
 questions: [],
 passing_score: 70,
 show_feedback: true
 }
 });
 };

 const editModuleContentSection = (section) => {
 setEditingModuleSectionId(section.id);
 setModuleContentSectionForm({
 type: section.type,
 title: section.title || '',
 video_url: section.video_url || '',
 text_content: section.text_content || '',
 markdown_content: section.markdown_content || '',
 pdf_url: section.pdf_url || '',
 presentation_url: section.presentation_url || '',
 quiz_data: section.quiz_data || {
 questions: [],
 passing_score: 70,
 show_feedback: true
 }
 });
 toast.info('Editing section - modify and click Update');
 };

 const removeModuleContentSection = (sectionId) => {
 setModuleForm({
 ...moduleForm,
 content_sections: moduleForm.content_sections.filter(s => s.id !== sectionId)
 });
 toast.success('Content section removed');
 };

 const cancelEditModuleSection = () => {
 setEditingModuleSectionId(null);
 setModuleContentSectionForm({
 type: 'video',
 title: '',
 video_url: '',
 text_content: '',
 markdown_content: '',
 pdf_url: '',
 presentation_url: '',
 quiz_data: {
 questions: [],
 passing_score: 70,
 show_feedback: true
 }
 });
 };

 const tierColors = {
 free: 'bg-green-100 text-green-800',
 pro: 'bg-amber-100 text-amber-800',
 business: 'bg-primary/10 text-primary',
 enterprise: 'bg-violet-100 text-violet-800',
 specialty: 'bg-rose-100 text-rose-800',
 paid: 'bg-cyan-100 text-cyan-800',
 // Legacy aliases so existing courses still render a badge
 tier_1: 'bg-amber-100 text-amber-800',
 tier_2: 'bg-primary/10 text-primary',
 tier_3: 'bg-cyan-100 text-cyan-800',
 };

 return (
 <div className="min-h-screen bg-background dark:from-neutral-900 dark:to-neutral-800">
 <ToolSelector />
 <div className="container mx-auto px-4 py-8 max-w-7xl">
 {/* Header */}
 <div className="mb-8">
 <Button
 variant="ghost"
 onClick={() => navigate('/admin')}
 className="mb-4"
 >
 <ArrowLeft className="w-4 h-4 mr-2" />
 Back to Admin Dashboard
 </Button>
 <div className="flex items-center gap-4 mb-4">
 <div className="h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
 <BookOpen className="w-7 h-7" />
 </div>
 <div>
 <span className="eyebrow">Admin · LMS</span>
 <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1 text-foreground">LMS Admin Portal</h1>
 <p className="text-muted-foreground text-sm">
 Manage courses, modules, lessons, and student support.
 </p>
 </div>
 </div>
 </div>

 {/* Compact liability advisory */}
 <div className="mb-6 rounded-2xl border border-primary/40 bg-primary/5 p-4">
 <p className="text-sm text-foreground/80 leading-relaxed">
 <strong className="text-foreground">Educational content.</strong> Freeze Dry Academy is not
 liable for the outcomes of applying these courses. Practice safe equipment operation and food
 safety at all times.
 </p>
 </div>

 {/* Advertisement Banner - Header */}
 <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg border-2 border-amber-300 dark:border-amber-700 shadow-sm">
 <div className="flex items-center justify-between gap-4">
 <div className="flex items-center gap-3">
 <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
 <Sparkles className="w-6 h-6 text-white" />
 </div>
 <div>
 <p className="text-sm font-bold text-amber-900 dark:text-amber-100">
 🎓 Build Engaging Courses for Your Students!
 </p>
 <p className="text-xs text-amber-700 dark:text-amber-300">
 Create professional content • Track student progress • Award certificates
 </p>
 </div>
 </div>
 </div>
 </div>

 {/* Ops shortcuts — Refresh Radar / Cohort Progress / Overlap Scan */}
 <div className="mb-6 grid gap-3 sm:grid-cols-3">
 <Link
 to="/admin/lms/refresh-radar"
 className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 hover:border-amber-500 transition-colors"
 data-testid="lms-nav-refresh-radar"
 >
 <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200 font-bold text-sm">
 🔔 Refresh Radar
 </div>
 <div className="text-xs text-muted-foreground mt-0.5">
 Every course + lesson whose review date is nearing, sorted by urgency.
 </div>
 </Link>
 <Link
 to="/admin/lms/cohort-progress"
 className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-3 hover:border-emerald-500 transition-colors"
 data-testid="lms-nav-cohort-progress"
 >
 <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-200 font-bold text-sm">
 📈 Cohort Progress
 </div>
 <div className="text-xs text-muted-foreground mt-0.5">
 Who's stuck where — completion, checklist gates, pass/needs-work.
 </div>
 </Link>
 <Link
 to="/admin/lms/overlap-scan"
 className="rounded-xl border border-violet-500/40 bg-violet-500/5 p-3 hover:border-violet-500 transition-colors"
 data-testid="lms-nav-overlap-scan"
 >
 <div className="flex items-center gap-2 text-violet-800 dark:text-violet-200 font-bold text-sm">
 🎯 Overlap Scan
 </div>
 <div className="text-xs text-muted-foreground mt-0.5">
 Semantic scan to catch overlapping courses before students notice.
 </div>
 </Link>
 </div>

 {/* Tabs */}
 <div className="flex gap-2 mb-6">
 <Button
 variant={activeTab === 'courses' ? 'default' : 'outline'}
 onClick={() => setActiveTab('courses')}
 >
 <BookOpen className="w-4 h-4 mr-2" />
 Courses
 </Button>
 <Button
 variant={activeTab === 'support' ? 'default' : 'outline'}
 onClick={() => setActiveTab('support')}
 >
 <MessageSquare className="w-4 h-4 mr-2" />
 Support Requests
 </Button>
 </div>

 {/* Courses Tab */}
 {activeTab === 'courses' && (
 <>
 {/* Curriculum Map — collapsible per-tier evaluation panel */}
 <CurriculumMap
 onBuildForTier={(t, seedTopic) => {
 setBlueprintSeed({
 topic: seedTopic || '',
 tier: t,
 notes: seedTopic
 ? `Focus this course on "${seedTopic}" — our KB shows only 1-2 existing lessons touch this area for the ${t} tier.`
 : '',
 });
 setShowBlueprintModal(true);
 }}
 onSuggestForTier={(t) => {
 setSuggestionTier(t);
 loadCourseSuggestions();
 // Scroll to suggestions panel once they load — small nudge so
 // admin sees them appear rather than hunting on the page.
 setTimeout(() => {
 document.querySelector('[data-testid="lms-suggestions-panel"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
 }, 400);
 }}
 onOpenCourse={(curriculumCourse) => {
                        // Route directly to the workspace — no scroll-to-top.
                        if (curriculumCourse?.id) navigate(`/admin/lms/course/${curriculumCourse.id}/workspace`);
                      }}
 />
 {/* Action Buttons */}
 <div className="flex gap-3 mb-6 flex-wrap">
 <Button
 onClick={() => { setBlueprintSeed(null); setShowBlueprintModal(true); }}
 className="bg-amber-500 hover:bg-amber-600 text-white"
 data-testid="lms-build-course-with-ai"
 >
 <Sparkles className="w-4 h-4 mr-2" />
 Build course with AI
 </Button>
 <div className="flex items-center gap-1.5">
 <select
 value={suggestionTier}
 onChange={(e) => setSuggestionTier(e.target.value)}
 disabled={suggestionsLoading}
 className="h-9 px-2 rounded-md border border-input bg-background text-sm"
 data-testid="lms-suggest-tier"
 title="Constrain AI suggestions to a specific tier"
 >
 <option value="any">Any tier</option>
 <option value="free">Free</option>
 <option value="pro">Pro</option>
 <option value="business">Business</option>
 <option value="enterprise">Enterprise</option>
 </select>
 <Button
 variant="outline"
 onClick={loadCourseSuggestions}
 disabled={suggestionsLoading}
 data-testid="lms-suggest-courses-btn"
 title="Ask AI which courses would fill the biggest gaps in your Knowledge Base"
 >
 <Sparkles className="w-4 h-4 mr-2 text-amber-500" />
 {suggestionsLoading ? 'Analyzing…' : (suggestions.length ? 'Refresh suggestions' : 'Suggest courses to build')}
 </Button>
 </div>
 <Button
 onClick={() => setShowCourseForm(!showCourseForm)}
 className="bg-blue-600 hover:bg-blue-700"
 >
 <Plus className="w-4 h-4 mr-2" />
 New Course
 </Button>
 <Button
 variant="outline"
 onClick={async () => {
   if (!window.confirm('Rewrite EVERY lesson body through the new markdown pipeline? Legacy `##` and `**` will become proper headings/bold. Bodies already rendering as HTML are skipped.')) return;
   try {
     const { data } = await axios.post(`${API}/admin/lms/rewrite-legacy-bodies`);
     toast.success(`Rewrote ${data.rewritten} lesson${data.rewritten === 1 ? '' : 's'} · ${data.skipped} already clean · ${data.checked} total`);
   } catch (e) {
     toast.error(e?.response?.data?.detail || 'Rewrite failed');
   }
 }}
 title="Re-render every lesson description through the new markdown pipeline so legacy `##`/`**` disappear"
 data-testid="lms-rewrite-legacy-btn"
 className="border-amber-400 text-amber-700 hover:bg-amber-50"
 >
 <Sparkles className="w-4 h-4 mr-2" />
 Rewrite legacy bodies
 </Button>
 <Button
 onClick={() => {
 // Pre-select course if one is selected, otherwise leave empty for user to choose
 setModuleForm({ 
 ...moduleForm, 
 course_id: selectedCourse?.id || '' 
 });
 setShowModuleForm(!showModuleForm);
 }}
 className="bg-green-600 hover:bg-green-700"
 >
 <Plus className="w-4 h-4 mr-2" />
 Add Module
 </Button>
 {selectedCourse && (
 <>
 {selectedModule && (
 <>
 <Button
 onClick={() => {
 setLessonForm({ ...lessonForm, module_id: selectedModule.id });
 setShowLessonForm(!showLessonForm);
 }}
 className="bg-primary hover:bg-primary/90"
 >
 <Plus className="w-4 h-4 mr-2" />
 Add Lesson
 </Button>
 {selectedLesson && (
 <Button
 onClick={() => {
 setQuizForm({ ...quizForm, lesson_id: selectedLesson.id });
 setShowQuizForm(!showQuizForm);
 }}
 className="bg-amber-600 hover:bg-amber-700"
 >
 <Plus className="w-4 h-4 mr-2" />
 Add Quiz
 </Button>
 )}
 </>
 )}
 </>
 )}
 </div>

 {/* Course Form */}
 {showCourseForm && (
 <CourseForm
 courseForm={courseForm}
 setCourseForm={setCourseForm}
 editingCourse={editingCourse}
 onSave={createCourse}
 onCancel={() => {
 setShowCourseForm(false);
 setEditingCourse(null);
 }}
 onOpenResearch={() => editingCourse && setResearchCourse({
   id: editingCourse.id,
   title: courseForm.title || editingCourse.title,
   abstract: courseForm.abstract,
 })}
 />
 )}

 {/* Course Research modal (KB-first + web enrichment) */}
 {researchCourse && (
 <CourseResearchModal
 course={researchCourse}
 onClose={() => setResearchCourse(null)}
 />
 )}

 {/* Module Form */}
 {showModuleForm && (
 <ModuleForm
 moduleForm={moduleForm}
 setModuleForm={setModuleForm}
 editingModule={editingModule}
 courses={courses}
 moduleContentSectionForm={moduleContentSectionForm}
 setModuleContentSectionForm={setModuleContentSectionForm}
 editingModuleSectionId={editingModuleSectionId}
 onSave={createModule}
 onCancel={() => setShowModuleForm(false)}
 onAddContentSection={addModuleContentSection}
 onEditContentSection={editModuleContentSection}
 onRemoveContentSection={removeModuleContentSection}
 onCancelEditSection={cancelEditModuleSection}
 />
 )}


 {/* Lesson Form */}
 {showLessonForm && (
 <LessonForm
 lessonForm={lessonForm}
 setLessonForm={setLessonForm}
 editingLesson={editingLesson}
 courses={courses}
 courseDetails={courseDetails}
 contentSectionForm={contentSectionForm}
 setContentSectionForm={setContentSectionForm}
 editingSectionId={editingSectionId}
 quizQuestion={quizQuestion}
 setQuizQuestion={setQuizQuestion}
 editingQuestionIndex={editingQuestionIndex}
 expandedSections={expandedSections}
 setExpandedSections={setExpandedSections}
 onSave={createLesson}
 onCancel={() => setShowLessonForm(false)}
 onAddContentSection={addContentSection}
 onEditContentSection={editContentSection}
 onCancelEditSection={cancelEditSection}
 onRemoveContentSection={removeContentSection}
 onMoveContentSection={moveContentSection}
 onAddQuizQuestion={() => {
 if (!quizQuestion.question || !quizQuestion.correct_answer) {
 toast.error('Please fill question and correct answer');
 return;
 }
 setContentSectionForm({
 ...contentSectionForm,
 quiz_data: {
 ...contentSectionForm.quiz_data,
 questions: [...contentSectionForm.quiz_data.questions, { ...quizQuestion }]
 }
 });
 setQuizQuestion({
 question: '',
 type: 'multiple_choice',
 options: ['', '', '', ''],
 correct_answer: '',
 explanation: ''
 });
 }}
 onEditQuizQuestion={editQuizQuestion}
 onUpdateQuizQuestion={updateQuizQuestion}
 onCancelEditQuizQuestion={cancelEditQuizQuestion}
 onRemoveQuizQuestion={(questionIndex) => {
 const questions = [...contentSectionForm.quiz_data.questions];
 questions.splice(questionIndex, 1);
 setContentSectionForm({
 ...contentSectionForm,
 quiz_data: { ...contentSectionForm.quiz_data, questions }
 });
 toast.success('Question removed');
 }}
 />
 )}


 {/* Quiz Form */}
 {showQuizForm && (
 <QuizForm
 quizForm={quizForm}
 setQuizForm={setQuizForm}
 newQuestion={newQuestion}
 setNewQuestion={setNewQuestion}
 onSave={createQuiz}
 onCancel={() => setShowQuizForm(false)}
 onAddQuestion={addQuestionToQuiz}
 onRemoveQuestion={removeQuestion}
 />
 )}


 {/* AI Content Health — surfaces AI-scaffolded courses whose
     lessons never received the Generate-content run so nothing
     ships as an empty stub by accident. */}
 <AIContentHealthCard />

 {/* Courses Overview — always visible, no analysis required */}
 <Card className="mb-4" data-testid="courses-overview">
 <CardContent className="p-4">
 <div className="flex flex-wrap items-center gap-2 mb-3">
 {(() => {
 const total = courses.length;
 const drafts = courses.filter((c) => !c.is_published).length;
 const published = total - drafts;
 const priv = courses.filter((c) => c.visibility === 'private').length;
 const stat = (label, value, testid) => (
 <div
 key={label}
 className="rounded-md border border-border bg-muted/40 px-3 py-1.5 text-center min-w-[70px]"
 data-testid={testid}
 >
 <div className="text-lg font-bold leading-none">{value}</div>
 <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{label}</div>
 </div>
 );
 return [
 stat('Total', total, 'stat-total'),
 stat('Published', published, 'stat-published'),
 stat('Drafts', drafts, 'stat-drafts'),
 stat('Private', priv, 'stat-private'),
 ];
 })()}
 <div className="flex-1" />
 <input
 type="text"
 value={coursesSearch}
 onChange={(e) => setCoursesSearch(e.target.value)}
 placeholder="Search courses by title…"
 className="h-9 px-3 rounded-md border border-input bg-background text-sm w-full md:w-64"
 data-testid="courses-search"
 />
 </div>
 <div className="flex flex-wrap items-center gap-1.5">
 {[
 { v: 'all',       l: 'All' },
 { v: 'drafts',    l: 'Drafts only' },
 { v: 'published', l: 'Published' },
 { v: 'private',   l: 'Private' },
 ].map((f) => (
 <button
 key={f.v}
 type="button"
 onClick={() => setCoursesFilter(f.v)}
 className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
 coursesFilter === f.v
 ? 'bg-primary text-primary-foreground border-primary'
 : 'bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 hover:border-primary'
 }`}
 data-testid={`courses-filter-${f.v}`}
 >
 {f.l}
 </button>
 ))}
 <span className="mx-1 h-4 w-px bg-slate-300 dark:bg-slate-600" />
 <select
 value={coursesTierFilter}
 onChange={(e) => setCoursesTierFilter(e.target.value)}
 className="h-7 text-xs px-2 rounded-md border border-input bg-background"
 data-testid="courses-tier-filter"
 title="Filter by tier"
 >
 <option value="all">All tiers</option>
 <option value="free">Free</option>
 <option value="pro">Pro</option>
 <option value="business">Business</option>
 <option value="enterprise">Enterprise</option>
 </select>
 </div>
 </CardContent>
 </Card>

 {/* Courses List */}
 <div className="space-y-4">
 {(() => {
 // Apply overview filters + search — filtering happens client-side
 // so the admin sees instant feedback without a network round-trip.
 const q = coursesSearch.trim().toLowerCase();
 const filtered = courses.filter((c) => {
 if (coursesFilter === 'drafts' && c.is_published) return false;
 if (coursesFilter === 'published' && !c.is_published) return false;
 if (coursesFilter === 'private' && c.visibility !== 'private') return false;
 if (coursesTierFilter !== 'all' && (c.tier || 'free') !== coursesTierFilter) return false;
 if (q && !(c.title || '').toLowerCase().includes(q)) return false;
 return true;
 });
 if (courses.length === 0) {
 return (
 <Card className="p-8 text-center">
 <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-400" />
 <p className="text-slate-600 dark:text-neutral-400">No courses yet. Create your first course!</p>
 </Card>
 );
 }
 if (filtered.length === 0) {
 return (
 <Card className="p-8 text-center" data-testid="courses-empty-filtered">
 <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-400" />
 <p className="text-slate-600 dark:text-neutral-400">No courses match your filters.</p>
 <button
 type="button"
 onClick={() => { setCoursesFilter('all'); setCoursesTierFilter('all'); setCoursesSearch(''); }}
 className="mt-3 text-xs text-primary hover:underline"
 >
 Clear filters
 </button>
 </Card>
 );
 }
 return filtered.map((course) => {
 const isExpanded = selectedCourse?.id === course.id;
 const details = courseDetails[course.id];
 
 return (
 <div key={course.id} data-testid={`admin-course-card-${course.id}`}>
 <CourseCard
 course={course}
 isExpanded={isExpanded}
 details={details}
 tierColors={tierColors}
 expandedModules={expandedModules}
 onToggle={() => toggleCourse(course)}
 onEdit={() => editCourse(course)}
 onDelete={() => deleteCourse(course.id)}
 onFillEmptyLessons={async (c) => {
 try {
 const { data } = await axios.post(`${API}/admin/lms/courses/${c.id}/fill-empty-lessons`);
 if (data.queued_modules === 0) {
 toast.info('Every lesson in this course already has generated content.');
 } else {
 toast.success(`Queued ${data.queued_modules} module${data.queued_modules === 1 ? '' : 's'} for content generation — will refresh when done.`);
 }
 } catch (err) {
 toast.error(err?.response?.data?.detail || 'Could not queue fill-empty job');
 }
 }}
 onAddModule={() => {
 setModuleForm({ ...moduleForm, course_id: course.id });
 setShowModuleForm(true);
 }}
 onToggleModule={toggleModule}
 onEditModule={editModule}
 onDeleteModule={deleteModule}
 onAddLesson={() => {}}
 onEditLesson={editLesson}
 onDeleteLesson={deleteLesson}
 onBatchGenerated={async () => {
 // AI batch job finished — refetch this course's details so
 // newly-generated lesson content shows up right away.
 if (selectedCourse) await fetchCourseDetails(selectedCourse.id);
 }}
 lessonForm={lessonForm}
 setLessonForm={setLessonForm}
 setShowLessonForm={setShowLessonForm}
 />
 </div>
 );
 });
 })()}
 </div>
 </>
 )}

 {/* Suggestions strip — shown right below action buttons once loaded. */}
 {suggestions.length > 0 && (
 <Card className="mb-6" data-testid="lms-suggestions-panel">
 <CardContent className="p-4">
 <p className="text-sm font-semibold mb-2 flex items-center gap-2">
 <Sparkles className="w-4 h-4 text-amber-500" />
 AI course suggestions — fills gaps in your Knowledge Base
 </p>
 <div className="grid gap-2 md:grid-cols-2">
 {suggestions.map((s, i) => (
 <div
 key={i}
 className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 hover:border-amber-400 cursor-pointer transition-colors"
 onClick={() => {
 // Seed the AI builder with everything the suggestion already
 // gave us — admins should never have to retype the topic/tier.
 setBlueprintSeed({
 topic: s.title,
 tier:  s.target_tier || 'pro',
 notes: [s.description, s.rationale ? `Why: ${s.rationale}` : null]
 .filter(Boolean).join('\n\n'),
 });
 setShowBlueprintModal(true);
 }}
 data-testid={`lms-suggestion-${i}`}
 >
 <div className="flex justify-between items-start gap-2">
 <p className="font-medium text-sm">{s.title}</p>
 <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
 s.priority === 'high' ? 'bg-rose-100 text-rose-800' :
 s.priority === 'medium' ? 'bg-amber-100 text-amber-800' :
 'bg-slate-100 text-slate-800'
 }`}>{s.priority}</span>
 </div>
 <p className="text-xs text-slate-500 mt-1 line-clamp-2">{s.description}</p>
 <p className="text-[11px] text-slate-400 mt-1 italic">Why: {s.rationale}</p>
 <div className="flex items-center justify-between mt-2">
 <p className="text-[10px] text-slate-500 uppercase tracking-wide">Tier: {s.target_tier}</p>
 <span className="text-[11px] text-amber-600 font-semibold flex items-center gap-1">
 <Sparkles className="w-3 h-3" /> Click to build
 </span>
 </div>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>
 )}

 {/* Support Requests Tab */}
 {activeTab === 'support' && (
 <SupportRequestsTab
 supportRequests={supportRequests}
 onUpdateRequest={updateSupportRequest}
 />
 )}

 {/* Advertisement Banner - Footer */}
 <div className="mt-8 p-6 bg-pro-soft rounded-lg border-2 border-primary/30 shadow-lg">
 <div className="text-center">
 <h3 className="text-xl font-bold text-foreground mb-2">
 🎓 Build the Best Learning Experience
 </h3>
 <p className="text-sm text-muted-foreground mb-4">
 Create engaging content that helps your students succeed
 </p>
 </div>
 </div>
 </div>
 <CourseBlueprintModal
 open={showBlueprintModal}
 onOpenChange={(v) => { setShowBlueprintModal(v); if (!v) setBlueprintSeed(null); }}
 onCreated={async (courseId) => {
 await fetchCourses();
 // Immediately expand + scroll to the freshly-built draft so the
 // admin never has to hunt for it in the list.
 if (courseId) setTimeout(() => openCourseInEditor(courseId), 350);
 }}
 seed={blueprintSeed}
 />
 </div>
 );
};

export default LMSAdmin;
